// Takes in a element and processes it into a very usable object vs the stored JSON.
// should handle all the logic currently in all of the properties panels.

import AppConstants from "@/constants/AppConstants";
import {
  FORM_STYLE_CHECKBOXES,
  FORM_STYLE_DROPDOWN,
  FORM_STYLE_FIELD,
  FORM_STYLE_RADIOS,
} from "@/constants/FormFieldConstants.mjs";
import BlockUpdateVO from "@/vo/BlockUpdate.mjs";
import DocumentStore from "@/document/DocumentStore";
import { COMMANDS } from "@/document/commands";
import { batchPropChanges } from "@/components/utils/PropsUpdater";
import { ButtonPropertyMapper } from "./ButtonPropertyMapper";
import { createEffectsPropertyMapper } from "./EffectsPropertyMaps";
import { createPropertyMapper } from "./BasePropertyMapper";
import {
  DEFAULT_VALUES_BY_STYLE,
  FORM_FIELD_CHECKBOX_META_MAP,
  FORM_FIELD_CHECKBOX_PROPS,
  FORM_FIELD_DROPDOWN_META_MAP,
  FORM_FIELD_DROPDOWN_PANEL_GROUPS,
  FORM_FIELD_RADIO_META_MAP,
  FORM_FIELD_RADIO_PROPS,
  FORM_FIELD_TEXT_FIELD_GROUPS,
  FORM_FIELD_TEXT_FIELD_META_MAP,
  FORM_STATE_OPTIONS,
  getFormFieldStateProps,
} from "./FormFieldPropertyMaps";
import { resolveFormFieldValue } from "../FormFieldStyleResolver";
import { getUpdateType } from "../PropUpdateUtils";
import {
  applyStyle,
  deviateProp,
  deviatePropFromUpdate,
  isElementStyled,
  isPropDeviated,
} from "../InheritanceUtils";
import { replaceSync } from "../MulticastUtils";
import { ensureArray } from "../JSUtils";
import { STYLE, SPLIT, DEVIATE } from "../../constants/PropUpdateConstants.mjs";
import { LAYOUT_DESKTOP, LAYOUT_MOBILE } from "@/constants/DesignConstants.mjs";
import { toggleSyncedProp } from "../SyncUtils";
import { propIsParent, gatherSubPropList } from "../Merge/MergeUtils.mjs";
import { LAYOUTS } from "@/constants/DesignConstants.mjs";
import UpdateBuilder from "../UpdateBuilder.mjs";
import { ELEMENT_PARENT_PROP_OVERRIDES } from "@/constants/SyncConstants";

/**
 * @typedef {import("@/vo/BlockUpdate.mjs").PropUpdate} PropUpdate
 * @typedef {import("immutable").Map} Map
 * @typedef {typeof import("@/constants/DesignConstants.mjs").LAYOUTS} LAYOUTS
 * @typedef {typeof SPLIT | typeof import("../../constants/PropUpdateConstants.mjs").MERGE} Intent
 */

const {
  ELEMENT_TYPE_BUTTON,
  ELEMENT_TYPE_SIMPLE,
  ELEMENT_TYPE_LINE,
  ELEMENT_TYPE_TEXT,
  ELEMENT_TYPE_HTML_TEXT,
  ELEMENT_TYPE_ICON,
  ELEMENT_TYPE_GRAPHIC,
  ELEMENT_TYPE_SVG,
  ELEMENT_TYPE_VIDEO,
  ELEMENT_TYPE_GALLERY,
  ELEMENT_TYPE_IFRAME,
  ELEMENT_TYPE_LOTTIE,
  ELEMENT_TYPE_FORM_INPUT,
  ELEMENT_TYPE_FORM_SELECT,
  ELEMENT_TYPE_FORM_CHECKBOX,
  ELEMENT_TYPE_FORM_RADIO,
} = AppConstants;

const FORM_TEXT_FIELD_PROPS = FORM_FIELD_TEXT_FIELD_GROUPS.flatMap(({ props }) => props);
const FORM_DROPDOWN_PANEL_PROPS = FORM_FIELD_DROPDOWN_PANEL_GROUPS.flatMap(({ props }) => props);

const UPDATE_EVENT_BASE = {
  action: undefined,
  addUndo: true,
  defaultValue: undefined,
  name: "",
  origValue: undefined,
  value: undefined,
  source: "propertyInput",
};

// this might be more readable as a class or builder.
// todo: once a class there could be a base strategy single/multi and go from there.
const onPropUpdateFactory =
  ({ elementId, addUndo = true, theme, updateOverrides = {}, onPropUpdate }) =>
  /**
   *
   * @param {string} name
   * @param {*} origValue
   * @param {LAYOUTS[number]} layout
   * @param {Map} element
   * @param {(props: import("./ButtonPropertyMaps").UpdateCallbackProps) => any} updateCallback
   * @returns {(e: CustomEvent) => void}
   */
  (name, origValue, layout, element, updateCallback) =>
  (e) => {
    const update = new BlockUpdateVO();

    const nameWithLayout = layout ? `${layout}.${name}` : name;
    let propInfo = {
      ...UPDATE_EVENT_BASE,
      ...updateOverrides,
      addUndo,
      value: e?.detail?.value,
      name: nameWithLayout,
      origValue,
    };

    // callback can be used to modify the propInfo or add additional updates
    updateCallback && (propInfo = updateCallback({ e, propInfo, theme, element }));

    // copied updater 👇
    // todo: maybe this can exist elsewhere? Might just need to be duplicated until the one from ElementProperties is eliminated.

    const updateType = getUpdateType({ elementData: element, propInfo, theme });

    const preparedUpdates =
      updateType === STYLE
        ? applyStyle(element, propInfo, layout)
        : updateType === SPLIT
          ? replaceSync(propInfo, element, SPLIT)
          : updateType === DEVIATE
            ? deviatePropFromUpdate(element, propInfo, theme)
            : ensureArray(propInfo);

    const updaterCB = (/** @type {Array<PropUpdate>} */ propUpdate) => {
      ensureArray(propUpdate).forEach((singleUpdate) =>
        update.addPropUpdate(elementId, singleUpdate),
      );
    };

    batchPropChanges(preparedUpdates, updaterCB);

    update.collapseUndos();

    DocumentStore.dispatch({ type: COMMANDS.UPDATE_BLOCK, payload: update });
  };

/**
 * Handles split/merge for a single prop. Adds or removes from the sync array if it can be synced.
 * @param {Object} props - The properties for handling split/merge.
 * @param {UpdateBuilder} props.builder - The builder object used for property updates.
 * @param {string} props.name - The name of the property.
 * @param {*} props.value - The value of the property.
 * @param {Intent} props.intent - The intent for the operation, either SPLIT or MERGE.
 * @param {boolean=} props.canMerge - Indicates if the property can be merged.
 * @returns {Object} - The updated builder object.
 */
const handleSplitMergeIntentProp = ({ builder, name, value, intent, canMerge = true }) => {
  intent === SPLIT
    ? builder.inheritProp(name, LAYOUT_MOBILE)
    : builder.removeUpdate({ name: `${LAYOUT_MOBILE}.${name}` }).addUpdate({
        name: `${LAYOUT_DESKTOP}.${name}`,
        value,
      });

  if (canMerge) {
    intent === SPLIT ? builder.removeMergeProp(name) : builder.addMergeProp(name);
  }

  return builder;
};

/**
 *
 * @param {Map} element
 * @param {PropUpdate & { intent: Intent, layout: LAYOUTS[number] }} intentUpdate
 * @param {Map} theme
 * @returns
 */
export const handleSplitMergeIntent = (element, intentUpdate, theme) => {
  const { name, value, intent, layout } = intentUpdate;

  const isStyled = isElementStyled(element);
  const isDeviating = !isPropDeviated(element, name, theme);

  let updates;

  // effectively deviate prop and add/remove mobile props to invert merge state in theme
  if (isStyled && isDeviating) {
    const builder = deviateProp(element, name, LAYOUT_DESKTOP, theme);
    const isPropParent = propIsParent(element, name);

    handleSplitMergeIntentProp({ builder, name, value, intent });

    if (isPropParent) {
      const subProps = gatherSubPropList(name);
      const intentLayout = intent === SPLIT ? LAYOUT_DESKTOP : layout;

      subProps.forEach((subPropName) => {
        // find update for current layout in the case of merge
        const subPropUpdate = builder.findUpdate({
          name: `${intentLayout}.${subPropName}`,
        });

        if (subPropUpdate) {
          handleSplitMergeIntentProp({
            builder,
            name: subPropName,
            value: subPropUpdate?.value,
            intent,
            canMerge: false,
          });
        }
      });
    }

    updates = builder.addUndo().collectChanges();
  } else {
    // regular split merge
    // todo: replace? Probably can take another stab at the base toggle with the new updater and collecting the direct value.
    toggleSyncedProp(
      element,
      // val is a list of resulting updates at the end of the toggle process sent to a callback (this function)
      (val) => (updates = val),
      layout,
      undefined,
      undefined,
      theme,
    )(name, undefined, value);
  }

  return updates;
};

// todo: update this to handle multiple elements
const prepareToggleSync = ({ elementId, element, theme }) => {
  return (/** @type {PropUpdate} */ propInfo) => {
    const update = new BlockUpdateVO();

    const propUpdates = handleSplitMergeIntent(element, propInfo, theme);

    const updaterCB = (/** @type {Array<PropUpdate>} */ propUpdate) => {
      ensureArray(propUpdate).forEach((singleUpdate) =>
        update.addPropUpdate(elementId, singleUpdate),
      );
    };

    batchPropChanges(propUpdates, updaterCB);

    update.collapseUndos();

    DocumentStore.dispatch({ type: COMMANDS.UPDATE_BLOCK, payload: update });
  };
};

/**
 * Generates prop removal updates for both layouts, and removes the prop from the sync array for a single element.
 * todo: handle hover props?
 * @param {Map} elementData
 * @param {string | Array<string>} propNames
 * @returns {Array<PropUpdate>}
 */
export const generateRemovePropUpdates = (elementData, propNames, elementParentPropOverrides) => {
  const propsToRemove = ensureArray(propNames).flatMap((propName) => {
    if (elementParentPropOverrides?.[propName]) return elementParentPropOverrides[propName];
    return propIsParent(elementData, propName)
      ? [propName, ...gatherSubPropList(propName)]
      : [propName];
  });

  const builder = new UpdateBuilder({ elementData });

  Array.from(new Set(propsToRemove)).forEach((propName) => {
    builder.removeMergeProp(propName);
    LAYOUTS.forEach((layout) => builder.deleteProp(propName, layout));
  });

  return builder.addUndo().collectChanges();
};

/**
 * Generates customization removal updates.
 * @param {{
 *  elementId: string,
 *  element: Map
 * }} props
 * @returns {(propNames: string | Array<string>) => void}
 */
const removeCustomization =
  ({ elementId, element }) =>
  (propNames) => {
    const update = new BlockUpdateVO();

    const elementParentPropOverrides = ELEMENT_PARENT_PROP_OVERRIDES[element.get("type")];
    // todo: update this for multiselect
    const preparedUpdates = generateRemovePropUpdates(
      element,
      propNames,
      elementParentPropOverrides,
    );

    const updaterCB = (/** @type {Array<PropUpdate>} */ propUpdate) => {
      ensureArray(propUpdate).forEach((singleUpdate) =>
        update.addPropUpdate(elementId, singleUpdate),
      );
    };

    batchPropChanges(preparedUpdates, updaterCB);

    update.collapseUndos();

    DocumentStore.dispatch({ type: COMMANDS.UPDATE_BLOCK, payload: update });
  };

/**
 *
 * @param {*} elem
 * @param {*} elementId
 * @param {*} theme
 * @param {*} layout
 * @param {Object=} metaMapOverride - override for the property meta map used by the mapper
 * @returns
 */
export const MapElementProperties = (elem, elementId, theme, layout, metaMapOverride) => {
  // catches selection edge case issues, prevents crashing
  if (!elem) return;

  const elementType = elem.get("type");
  const propertyMapper = ElementPropertyMapperMap[elementType];

  if (!propertyMapper) {
    return elem;
  }

  // todo: is there a way/map to detect if a prop will deviate beforehand? Not just scoped and checked per update.

  const _onPropUpdate = onPropUpdateFactory({ elementId, addUndo: true, theme });
  const _onPropUpdateWithoutUndo = onPropUpdateFactory({ elementId, addUndo: false, theme });
  const _onToggleSync = prepareToggleSync({ elementId, element: elem, theme });
  const _onCustomizationRemoval = removeCustomization({ elementId, element: elem });

  return propertyMapper(
    elem,
    _onPropUpdate,
    _onPropUpdateWithoutUndo,
    theme,
    layout,
    _onToggleSync,
    _onCustomizationRemoval,
    metaMapOverride,
  );
};

// Map the planet
const ElementPropertyMapperMap = {
  [ELEMENT_TYPE_BUTTON]: ButtonPropertyMapper,
  [ELEMENT_TYPE_SIMPLE]: createEffectsPropertyMapper(ELEMENT_TYPE_SIMPLE),
  [ELEMENT_TYPE_LINE]: createEffectsPropertyMapper(ELEMENT_TYPE_LINE),
  [ELEMENT_TYPE_TEXT]: createEffectsPropertyMapper(ELEMENT_TYPE_TEXT),
  [ELEMENT_TYPE_HTML_TEXT]: createEffectsPropertyMapper(ELEMENT_TYPE_TEXT),
  [ELEMENT_TYPE_ICON]: createEffectsPropertyMapper(ELEMENT_TYPE_ICON),
  [ELEMENT_TYPE_GRAPHIC]: createEffectsPropertyMapper(ELEMENT_TYPE_GRAPHIC),
  [ELEMENT_TYPE_SVG]: createEffectsPropertyMapper(ELEMENT_TYPE_SVG),
  [ELEMENT_TYPE_VIDEO]: createEffectsPropertyMapper(ELEMENT_TYPE_VIDEO),
  [ELEMENT_TYPE_GALLERY]: createEffectsPropertyMapper(ELEMENT_TYPE_GALLERY),
  [ELEMENT_TYPE_IFRAME]: createEffectsPropertyMapper(ELEMENT_TYPE_IFRAME),
  [ELEMENT_TYPE_LOTTIE]: createEffectsPropertyMapper(ELEMENT_TYPE_LOTTIE),
  [ELEMENT_TYPE_FORM_INPUT]: createPropertyMapper({
    propertyMetaMap: FORM_FIELD_TEXT_FIELD_META_MAP,
    customizableEffects: FORM_STATE_OPTIONS.flatMap(({ value }) =>
      getFormFieldStateProps(FORM_TEXT_FIELD_PROPS, value),
    ),
    defaultValues: DEFAULT_VALUES_BY_STYLE[FORM_STYLE_FIELD],
    resolveValue: resolveFormFieldValue,
  }),
  [ELEMENT_TYPE_FORM_SELECT]: createPropertyMapper({
    propertyMetaMap: FORM_FIELD_DROPDOWN_META_MAP,
    customizableEffects: FORM_STATE_OPTIONS.flatMap(({ value }) =>
      getFormFieldStateProps(FORM_DROPDOWN_PANEL_PROPS, value),
    ),
    defaultValues: DEFAULT_VALUES_BY_STYLE[FORM_STYLE_DROPDOWN],
    resolveValue: resolveFormFieldValue,
  }),
  [ELEMENT_TYPE_FORM_RADIO]: createPropertyMapper({
    propertyMetaMap: FORM_FIELD_RADIO_META_MAP,
    customizableEffects: FORM_STATE_OPTIONS.flatMap(({ value }) =>
      getFormFieldStateProps(FORM_FIELD_RADIO_PROPS, value),
    ),
    defaultValues: DEFAULT_VALUES_BY_STYLE[FORM_STYLE_RADIOS],
    resolveValue: resolveFormFieldValue,
  }),
  [ELEMENT_TYPE_FORM_CHECKBOX]: createPropertyMapper({
    propertyMetaMap: FORM_FIELD_CHECKBOX_META_MAP,
    customizableEffects: FORM_STATE_OPTIONS.flatMap(({ value }) =>
      getFormFieldStateProps(FORM_FIELD_CHECKBOX_PROPS, value),
    ),
    defaultValues: DEFAULT_VALUES_BY_STYLE[FORM_STYLE_CHECKBOXES],
    resolveValue: resolveFormFieldValue,
  }),
};

export default MapElementProperties;
